import React, { Fragment, useEffect, useRef, useState, useMemo } from 'react';
import { Button, Icon, Popup } from 'semantic-ui-react';
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { doThrow } from "../../../pages/Darts/Scoring/DartsScoring.thunk";
import { IThrowPayloadData } from "../../../pages/Darts/Scoring/DartsScoring.types";
import { DartboardProps, DartboardSettings, DartboardThrow, DartboardSector } from "./Dartboard.types";
import styles from "./Dartboard.module.scss";

const DartBoard: React.FC<DartboardProps> = ({ type, mode, inverted }) => {
    // Устанавливаем значение по умолчанию
    const _type = validateType();
    const _mode = mode || 'scorring';
    const _inverted = inverted || false;
    // привязываем ref к конкретному элементу
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    // костанты
    const numbers = [14, 9, 12, 5, 20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11];
    const halfSector = Math.PI / numbers.length;
    const zero = halfSector - Math.PI;
    const ringSI = 3.5;
    const ringS = 3;
    const ringBE = 0.8;
    const ringNum = 1;
    const ringMult = 1;
    const baseColors = useMemo(() => (_inverted ? ["#fffff0", "#000000"] : ["#000000", "#fffff0"]), [_inverted]);
    const doubleColors = ['#ff0000', '#008000'];
    const tripleColors = ['#ff0000', '#008000'];
    const bullseye50Color = '#ff0000';
    const bullseye25Color = '#008000';
    const strokeColor = '#aaa';
    const canvasColor = '#000000';
    const numColor = '#000';
    const sectors = sectorAngles();
    const [size, setSize] = useState<DartboardSettings>({ width: window.innerWidth * 10 / 16 - 20, height: window.innerHeight - 100 });
    const thickness = useMemo(() => validateThickness(), [size]);

    const dispatch = useAppDispatch()
    const scoring = useAppSelector(state => state.DartsScoring);

    useEffect(() => {
        window.addEventListener('resize', resizeCanvas);
        return () => window.removeEventListener('resize', resizeCanvas);
    }, []);

    useEffect(() => {
        drawBoard();
    }, [size]);

    const handleClick = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left - centerX;
        const y = e.clientY - rect.top - centerY;

        const throwData = detectThrow(x, y);
        if (!throwData) return;
        dispatchThrow(throwData);
    }

    function detectThrow(x: number, y: number): DartboardThrow | null {
        const distance = Math.sqrt(x * x + y * y);
        let throwData: DartboardThrow = {
            ring: "none",
            sector: 0,
            score: 0
        }
        let radius = ringBE * thickness;
        if (distance <= radius) {
            throwData.ring = "doubleBull";
            throwData.score = 50;
            return throwData;
        }
        radius += ringMult * thickness;
        if (distance <= radius) {
            throwData.ring = "singleBull";
            throwData.score = 25;
            return throwData;
        }

        let angle = Math.atan2(y, x);
        if (angle < zero) {
            angle += 2 * Math.PI;
        };
        angle += Math.PI - halfSector;

        const hitSector = sectors.find(sector => angle >= sector.start && angle < sector.end);
        if (hitSector) {
            throwData.sector = hitSector.value;
            radius += ringSI * thickness;
            if (distance <= radius) {
                throwData.ring = "singleInner";
                throwData.score = hitSector.value;
                return throwData;
            }
            if (type === 'quadro') {
                radius += ringMult * thickness;
                if (distance <= radius) {
                    throwData.ring = "quadro";
                    throwData.score = hitSector.value * 4;
                    return throwData;
                }
                radius += ringS * thickness;
                if (distance <= radius) {
                    throwData.ring = "singleMiddle";
                    throwData.score = hitSector.value;
                    return throwData;
                }
            }
            radius += ringMult * thickness;
            if (distance <= radius) {
                throwData.ring = "triple";
                throwData.score = hitSector.value * 3;
                return throwData;
            }
            radius += ringS * thickness;
            if (distance <= radius) {
                throwData.ring = "singleOuter";
                throwData.score = hitSector.value;
                return throwData;
            }
            radius += ringMult * thickness;
            if (distance <= radius) {
                throwData.ring = "double";
                throwData.score = hitSector.value * 2;
                return throwData;
            }
            radius += ringMult * thickness;
            if (distance <= radius) {
                throwData.ring = "milk";
                return throwData;
            }
        }
        radius += ringMult * thickness;
        if (distance <= radius) {
            throwData.sector = 0;
            return throwData;
        }
        return null;
    }

    function onBounceOut () {
        let throwData: DartboardThrow = { ring: "bounceOut", sector: 0, score: 0 };
        dispatchThrow(throwData);
    }

    function dispatchThrow(throwData: DartboardThrow) {
        let props: IThrowPayloadData = {
            ring: throwData.ring,
            sector: throwData.sector,
            score: throwData.score
        }
        dispatch(doThrow(props));
    }

    function resizeCanvas() {
        let canvas = canvasRef.current;
        if (!canvas) return;
        let rect = canvas.getBoundingClientRect();
        const dataPayload: DartboardSettings = {
            width: window.innerWidth * 10 / 16 - 2 * rect.left, // расстояние от левого края окна
            height: window.innerHeight - rect.top - rect.left // расстояние от верхнего края окна
        };
        setSize(dataPayload);
        // window.scrollX window.scrollY - для получения текущей прокрутки окна
    }

    function validateType(): string {
        if (!type || !['triple', 'quadro', 'sectors', 'rings'].includes(type)) {
            return 'triple';
        } else {
            return type;
        }
    }

    //// Инициализируем состояние с типом и режимом
    //const init = (data: DartboardData) => {
    //    type = data.type; mode = data.mode; inverted = data.inverted;
    //};

    function validateThickness(): number {
        let minDimensions = Math.min(size.width, size.height);
        switch (_type) {
            case 'quadro':
                return minDimensions / (2 * (4 * ringMult + ringNum + 2 * ringS + ringSI + ringBE));
            case 'sectors':
                return minDimensions / 2;
            case 'rings':
                return minDimensions / (2 * 10);
            case 'triple':
            default:
                return minDimensions / (2 * (3 * ringMult + ringNum + ringS + ringSI + ringBE));
        }
    }

    function sectorAngles(): DartboardSector[] {
        return numbers.map((value, i) => ({
            value,
            start: (i * 2 * Math.PI) / numbers.length,
            end: ((i + 1) * 2 * Math.PI) / numbers.length
        }));
    }

    function drawRing(context: CanvasRenderingContext2D, innerR: number, outerR: number, color: string, stroke: boolean) {
        context.beginPath();
        const centerX = size.width / 2;
        const centerY = size.height / 2;
        context.arc(centerX, centerY, outerR, 0, 2 * Math.PI);
        context.arc(centerX, centerY, innerR, 0, 2 * Math.PI, true);
        context.closePath();
        context.fillStyle = color;
        context.fill();
        if (stroke) {
            context.lineWidth = 1;
            context.strokeStyle = strokeColor;
            context.stroke();
        }
    }

    function drawBullseye(context: CanvasRenderingContext2D, radius: number, color: string) {
        context.beginPath();
        const centerX = size.width / 2;
        const centerY = size.height / 2;
        context.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        context.closePath();
        context.fillStyle = color;
        context.fill();
        context.lineWidth = 1;
        context.strokeStyle = strokeColor;
        context.stroke();
    }

    function drawSectorRing(context: CanvasRenderingContext2D, innerR: number, outerR: number, startAngle: number, endAngle: number, color: string) {
        context.beginPath();
        const centerX = size.width / 2;
        const centerY = size.height / 2;
        context.arc(centerX, centerY, outerR, startAngle, endAngle);
        context.arc(centerX, centerY, innerR, endAngle, startAngle, true);
        context.closePath();
        context.fillStyle = color;
        context.fill();
        context.lineWidth = 1;
        context.strokeStyle = strokeColor;
        context.stroke();
    }

    function drawBoard() {
        let canvas = canvasRef.current;
        if (!canvas) return;
        // Устанавливаем внутреннее разрешение canvas
        canvas.width = size.width;
        canvas.height = size.height;
        const context = canvas.getContext('2d');
        if (!context) return;
        context.fillStyle = canvasColor;
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.lineWidth = 1;
        context.strokeStyle = strokeColor;

        let radiusIn = 0;
        let radiusOut = ringBE * thickness;
        drawBullseye(context, radiusOut, bullseye50Color);
        radiusIn = radiusOut;
        radiusOut = radiusIn + ringMult * thickness;
        drawRing(context, radiusIn, radiusOut, bullseye25Color, false);

        let radiusBase = radiusOut;
        for (let i = 0; i < numbers.length; i++) {
            const angleStart = sectors[i].start + zero;
            const angleEnd = sectors[i].end + zero;

            radiusIn = radiusBase;
            radiusOut = radiusIn + ringSI * thickness;
            drawSectorRing(context, radiusIn, radiusOut, angleStart, angleEnd, baseColors[i % 2]);
            if (type === 'quadro') {
                radiusIn = radiusOut;
                radiusOut = radiusIn + ringMult * thickness;
                drawSectorRing(context, radiusIn, radiusOut, angleStart, angleEnd, tripleColors[i % 2]);
                radiusIn = radiusOut;
                radiusOut = radiusIn + ringS * thickness;
                drawSectorRing(context, radiusIn, radiusOut, angleStart, angleEnd, baseColors[i % 2]);
            }
            radiusIn = radiusOut;
            radiusOut = radiusIn + ringMult * thickness;
            drawSectorRing(context, radiusIn, radiusOut, angleStart, angleEnd, tripleColors[i % 2]);
            radiusIn = radiusOut;
            radiusOut = radiusIn + ringS * thickness;
            drawSectorRing(context, radiusIn, radiusOut, angleStart, angleEnd, baseColors[i % 2]);
            radiusIn = radiusOut;
            radiusOut = radiusIn + ringMult * thickness;
            drawSectorRing(context, radiusIn, radiusOut, angleStart, angleEnd, doubleColors[i % 2]);
            radiusIn = radiusOut;
            radiusOut = radiusIn + ringMult * thickness;
            drawSectorRing(context, radiusIn, radiusOut, angleStart, angleEnd, numColor);

            radiusIn = radiusOut;
            radiusOut = radiusIn + ringMult * thickness;
            const midAngle = (angleStart + angleEnd) / 2;
            //ToDo: пересчитать положение цифр в зависимости от размера доски
            let textOffser = type === 'quadro' ? 10 : 20;
            const centerX = size.width / 2;
            const centerY = size.height / 2;
            const textX = centerX + (radiusIn - textOffser) * Math.cos(midAngle);
            const textY = centerY + (radiusIn - textOffser) * Math.sin(midAngle);
            context.fillStyle = '#fff';
            //ToDo: пересчитать размер шрифта в зависимости от размера доски
            //context.font = "24px bold 'Lato, Arial, Helvetica, sans-serif'";
            let fontSize = Math.max(12, Math.min(24, Math.floor(thickness / 2)));
            context.font = `${fontSize}px Lato`;
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText(numbers[i].toString(), textX, textY);
        }
    }

    return (
        <Fragment>
            <canvas ref={canvasRef} onClick={handleClick} />
            <Popup className={styles.btnBounce} content="Bounce Out" position="top right"
                trigger={
                    <Button icon color="blue" onClick={onBounceOut} disabled={scoring.btnUndoDisabled}>
                        <Icon name='external alternate' />
                    </Button>
                }
            />
        </Fragment>
    );
};

export default DartBoard;

// <canvas ref={canvasRef} width={width} height={height} onClick={handleClick} />

// /**
// * Dartboard SDC (Standard Dartboard Configuration)
// * Bullseye (Inner Bull)	12.7 mm	Scores 50 points
// * Outer Bull	31.8 mm	Scores 25 points
// * Treble Ring	8 mm width, 107 mm from center	Triple score of segment hit
// * Double Ring	8 mm width, 170 mm from center	Double score of segment hit
// * Number Ring	~25.4 mm tall numbers	Displays numbers 1–20
// * Board Diameter	451 mm (±10 mm)	Standard regulation size
// * Wire Thickness	1.5 mm
// */
//
// /** Советы
//  * Используй canvas.style.width = '100%' и canvas.style.height = '100%' для адаптивного отображения.
//  * Используй canvas.width = container.clientWidth и canvas.height = container.clientHeight для корректного внутреннего разрешения.
//  * Не задавай фиксированные размеры canvas в пикселях через CSS — это может мешать адаптивности.
// */
//
// /**
//  * Можно использовать CSS-переменные, которые задаются в React и используются в SCSS:
//  * SCSS использует переменные, а React управляет их значениями.
//  *
//  * jsx
//  * function ThemedBox({ themeColor }) {
//  *   return (
//  *     <div style={{ '--theme-color': themeColor }} className="box">
//  *       Hello
//  *     </div>
//  *   );
//  * }
//  *
//  * scss
//  * // styles.scss
//  * .box {
//  *   background-color: var(--theme-color);
//  * }
//  */


// import React, {
//     Fragment,
//     useCallback,
//     useEffect,
//     useMemo,
//     useRef,
//     useState,
// } from "react";
// import { useAppDispatch } from "../../../hooks/hooks";
// import { testThrow } from "../../../store/dartsGame/currentGame/dartsCurrentGameSlice";
// import { IThrowPayload } from "../../../store/dartsGame/currentGame/types";
// import { GameTypes } from "../../../common/enums/gameEvents";
// // import { Button, Icon } from "semantic-ui-react"; // если нужно
// import styles from "./Dartboard.module.scss";
// import { DartboardProps, DartboardData, DartsThrowType } from "./Dartboard.types";
// import { DartboardSector } from "./Dartboard.interfaces";
//
// const numbers = [14, 9, 12, 5, 20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11];
//
// const DartBoard: React.FC<DartboardProps> = ({ type, inverted }) => {
//     // значения по умолчанию
//     const _type = validateType(type);
//     const _inverted = inverted || false;
//
//     const canvasRef = useRef<HTMLCanvasElement | null>(null);
//
//
//     const halfSector = Math.PI / numbers.length;
//     const zero = halfSector - Math.PI;
//     const ringSI = 3.5;
//     const ringS = 3;
//     const ringBE = 0.8;
//     const ringNum = 1;
//     const ringMult = 1;
//
//
//     const baseColors = useMemo(() => (_inverted ? ["#fffff0", "#000000"] : ["#000000", "#fffff0"]), [_inverted]);
//     const doubleColors = ["#ff0000", "#008000"];
//     const tripleColors = ["#ff0000", "#008000"];
//     const bullseye50Color = "#ff0000";
//     const bullseye25Color = "#008000";
//     const strokeColor = "#aaa";
//     const canvasColor = "#000000";
//     const numColor = "#000";
//
//     const [data, setData] = useState<DartboardData>(() => ({
//         width: Math.max(100, window.innerWidth * 10 / 16 - 20),
//         height: Math.max(100, window.innerHeight - 94),
//     }));
//
//
//     const thickness = useMemo(() => {
//         const minDim = Math.min(data.width, data.height);
//         switch (_type) {
//             case "quadro":
//                 return minDim / (2 * (4 * ringMult + ringNum + 2 * ringS + ringSI + ringBE));
//             case "sectors":
//                 return minDim / 2;
//             case "rings":
//                 return minDim / (2 * 10);
//             case "triple":
//             default:
//                 return minDim / (2 * (3 * ringMult + ringNum + ringS + ringSI + ringBE));
//         }
//     }, [data.width, data.height, _type]);
//
//
//     const sectors: DartboardSector[] = useMemo(() => {
//         return numbers.map((value, i) => ({
//             value,
//             start: (i * 2 * Math.PI) / numbers.length,
//             end: ((i + 1) * 2 * Math.PI) / numbers.length,
//         }));
//     }, []);
//
//     const dispatch = useAppDispatch();
//
//
//     const sendThrow = useCallback(
//         async (payload: DartsThrowType) => {
//             const gameThrowData = {
//                 gameId: 2,
//                 playerId: 2,
//                 type: 0,
//                 bed: "S20",
//                 score: payload.score,
//                 date: new Date(),
//                 ring: payload.ring,
//             };
//             const testThrowData: IThrowPayload = {
//                 gameData: gameThrowData,
//                 gameType: GameTypes.LEAGUE,
//             };
//             await dispatch(testThrow(testThrowData));
//             alert(JSON.stringify(payload));
//         },
//         [dispatch]
//     );
//
//
//     const handleClick = useCallback(
//         (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
//             const canvas = canvasRef.current;
//             if (!canvas) return;
//
//             const rect = canvas.getBoundingClientRect();
//             const centerX = data.width / 2;
//             const centerY = data.height / 2;
//             const x = e.clientX - rect.left - centerX;
//             const y = e.clientY - rect.top - centerY;
//             const distance = Math.hypot(x, y);
//
//             let radius = ringBE * thickness;
//
//
//             if (distance <= radius) {
//                 return void sendThrow({ ring: "doubleBull", sector: 0, score: 50 });
//             }
//
//
//             radius += ringMult * thickness;
//             if (distance <= radius) {
//                 return void sendThrow({ ring: "singleBull", sector: 0, score: 25 });
//             }
//
//             let angle = Math.atan2(y, x);
//             if (angle < zero) angle += 2 * Math.PI;
//             angle += Math.PI - halfSector;
//
//             const hitSector = sectors.find((s) => angle >= s.start && angle < s.end);
//
//             if (hitSector) {
//
//                 radius += ringSI * thickness;
//                 if (distance <= radius) {
//                     return void sendThrow({ ring: "singleInner", sector: hitSector.value, score: hitSector.value });
//                 }
//
//
//                 if (_type === "quadro") {
//                     radius += ringMult * thickness;
//                     if (distance <= radius) {
//                         return void sendThrow({ ring: "quadro", sector: hitSector.value, score: hitSector.value * 4 });
//                     }
//
//                     radius += ringS * thickness;
//                     if (distance <= radius) {
//                         return void sendThrow({ ring: "singleMiddle", sector: hitSector.value, score: hitSector.value });
//                     }
//                 }
//
//
//                 radius += ringMult * thickness;
//                 if (distance <= radius) {
//                     return void sendThrow({ ring: "triple", sector: hitSector.value, score: hitSector.value * 3 });
//                 }
//
//
//                 radius += ringS * thickness;
//                 if (distance <= radius) {
//                     return void sendThrow({ ring: "singleOuter", sector: hitSector.value, score: hitSector.value });
//                 }
//
//
//                 radius += ringMult * thickness;
//                 if (distance <= radius) {
//                     return void sendThrow({ ring: "double", sector: hitSector.value, score: hitSector.value * 2 });
//                 }
//
//                 radius += ringMult * thickness;
//                 if (distance <= radius) {
//                     return void sendThrow({ ring: "milk", sector: hitSector.value, score: 0 });
//                 }
//             }
//
//             return void sendThrow({ ring: "none", sector: 0, score: 0 });
//         },
//         [data.width, data.height, thickness, sectors, _type, sendThrow, halfSector, zero]
//     );
//
//     const drawRing = useCallback(
//         (ctx: CanvasRenderingContext2D, innerR: number, outerR: number, color: string, stroke: boolean) => {
//             ctx.beginPath();
//             const centerX = data.width / 2;
//             const centerY = data.height / 2;
//             ctx.arc(centerX, centerY, outerR, 0, 2 * Math.PI);
//             ctx.arc(centerX, centerY, innerR, 0, 2 * Math.PI, true);
//             ctx.closePath();
//             ctx.fillStyle = color;
//             ctx.fill();
//             if (stroke) {
//                 ctx.lineWidth = 1;
//                 ctx.strokeStyle = strokeColor;
//                 ctx.stroke();
//             }
//         },
//         [data.width, data.height]
//     );
//
//     const drawBullseye = useCallback(
//         (ctx: CanvasRenderingContext2D, radius: number, color: string) => {
//             ctx.beginPath();
//             const centerX = data.width / 2;
//             const centerY = data.height / 2;
//             ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
//             ctx.closePath();
//             ctx.fillStyle = color;
//             ctx.fill();
//             ctx.lineWidth = 1;
//             ctx.strokeStyle = strokeColor;
//             ctx.stroke();
//         },
//         [data.width, data.height]
//     );
//
//     const drawSectorRing = useCallback(
//         (
//             ctx: CanvasRenderingContext2D,
//             innerR: number,
//             outerR: number,
//             startAngle: number,
//             endAngle: number,
//             color: string
//         ) => {
//             ctx.beginPath();
//             const centerX = data.width / 2;
//             const centerY = data.height / 2;
//             ctx.arc(centerX, centerY, outerR, startAngle, endAngle);
//             ctx.arc(centerX, centerY, innerR, endAngle, startAngle, true);
//             ctx.closePath();
//             ctx.fillStyle = color;
//             ctx.fill();
//             ctx.lineWidth = 1;
//             ctx.strokeStyle = strokeColor;
//             ctx.stroke();
//         },
//         [data.width, data.height]
//     );
//
//     const drawBoard = useCallback(() => {
//         const canvas = canvasRef.current;
//         if (!canvas) return;
//         const dpr = window.devicePixelRatio || 1;
//         canvas.width = Math.floor(data.width * dpr);
//         canvas.height = Math.floor(data.height * dpr);
//         canvas.style.width = `${data.width}px`;
//         canvas.style.height = `${data.height}px`;
//
//         const ctx = canvas.getContext("2d");
//         if (!ctx) return;
//
//         ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
//
//
//         ctx.fillStyle = canvasColor;
//         ctx.fillRect(0, 0, data.width, data.height);
//         ctx.lineWidth = 1;
//         ctx.strokeStyle = strokeColor;
//
//         let radiusIn = 0;
//         let radiusOut = ringBE * thickness;
//         drawBullseye(ctx, radiusOut, bullseye50Color);
//         radiusIn = radiusOut;
//         radiusOut = radiusIn + ringMult * thickness;
//         drawRing(ctx, radiusIn, radiusOut, bullseye25Color, false);
//
//         const radiusBase = radiusOut;
//
//
//         for (let i = 0; i < numbers.length; i++) {
//             const angleStart = sectors[i].start + zero;
//             const angleEnd = sectors[i].end + zero;
//
//             radiusIn = radiusBase;
//             radiusOut = radiusIn + ringSI * thickness;
//             drawSectorRing(ctx, radiusIn, radiusOut, angleStart, angleEnd, baseColors[i % 2]);
//
//
//             if (_type === "quadro") {
//                 radiusIn = radiusOut;
//                 radiusOut = radiusIn + ringMult * thickness;
//                 drawSectorRing(ctx, radiusIn, radiusOut, angleStart, angleEnd, tripleColors[i % 2]);
//                 radiusIn = radiusOut;
//                 radiusOut = radiusIn + ringS * thickness;
//                 drawSectorRing(ctx, radiusIn, radiusOut, angleStart, angleEnd, baseColors[i % 2]);
//             }
//
//
//             radiusIn = radiusOut;
//             radiusOut = radiusIn + ringMult * thickness;
//             drawSectorRing(ctx, radiusIn, radiusOut, angleStart, angleEnd, tripleColors[i % 2]);
//
//
//             radiusIn = radiusOut;
//             radiusOut = radiusIn + ringS * thickness;
//             drawSectorRing(ctx, radiusIn, radiusOut, angleStart, angleEnd, baseColors[i % 2]);
//
//
//             radiusIn = radiusOut;
//             radiusOut = radiusIn + ringMult * thickness;
//             drawSectorRing(ctx, radiusIn, radiusOut, angleStart, angleEnd, doubleColors[i % 2]);
//
//
//             radiusIn = radiusOut;
//             radiusOut = radiusIn + ringMult * thickness;
//             drawSectorRing(ctx, radiusIn, radiusOut, angleStart, angleEnd, numColor);
//
//
//             radiusIn = radiusOut;
//             radiusOut = radiusIn + ringMult * thickness;
//             const midAngle = (angleStart + angleEnd) / 2;
//             const textOffset = _type === "quadro" ? 10 : 20;
//             const cx = data.width / 2;
//             const cy = data.height / 2;
//             const textX = cx + (radiusIn - textOffset) * Math.cos(midAngle);
//             const textY = cy + (radiusIn - textOffset) * Math.sin(midAngle);
//             ctx.fillStyle = "#fff";
//             ctx.font = "20px Verdana";
//             ctx.textAlign = "center";
//             ctx.textBaseline = "middle";
//             ctx.fillText(numbers[i].toString(), textX, textY);
//         }
//     }, [
//         data.width,
//         data.height,
//         thickness,
//         baseColors,
//         _type,
//         sectors,
//         drawBullseye,
//         drawRing,
//         drawSectorRing,
//         zero,
//     ]);
//
//
//     const resizeCanvas = useCallback(() => {
//         const rect = canvasRef.current?.getBoundingClientRect();
//         const left = rect?.left ?? 0;
//         const top = rect?.top ?? 0;
//
//         setData({
//             width: Math.max(100, window.innerWidth * 10 / 16 - 2 * left),
//             height: Math.max(100, window.innerHeight - top - left),
//         });
//     }, []);
//
//
//     useEffect(() => {
//         window.addEventListener("resize", resizeCanvas);
//         resizeCanvas();
//         return () => window.removeEventListener("resize", resizeCanvas);
//     }, [resizeCanvas]);
//
//
//     useEffect(() => {
//         drawBoard();
//     }, [drawBoard]);
//
//     return (
//         <Fragment>
//             <canvas ref={canvasRef} onClick={handleClick} />
//             {/* <Button color="blue" onClick={() => sendThrow({ ring: "bounceOut", sector: 0, score: 0 })}>
//         Bounce Out
//       </Button> */}
//         </Fragment>
//     );
// };
//
// export default DartBoard;
//
//
//
// function validateType(type?: DartboardProps["type"]): NonNullable<DartboardProps["type"]> {
//     if (!type || !["triple", "quadro", "sectors", "rings"].includes(type)) {
//         return "triple";
//     }
//     return type as NonNullable<DartboardProps["type"]>;
// }
